import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { billingErrorToast } from '@components/BillingError/toast';
import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import { useAddPaymentMethod, useSubscriptionStatus } from '@hooks';
import { root_path, subscribe_path, update_payment_path } from '@routes';
import { useStripe } from '@stripe/react-stripe-js';
import { SetupIntent, StripeError } from '@stripe/stripe-js';
import { trackSubscriptionInGaAndAhoy } from '@tracking';
import { BillingErrorPayload, normalizeBillingError } from '@utils/billingErrors';
import { extractQueryParams } from '@utils/queryStringHelper';
import { stripeSetupErrorPayload } from '@utils/stripePaymentError';

import styles from './styles.module.scss';

enum StripeStatus {
  Succeeded = 'succeeded',
  Processing = 'processing',
  RequiresPaymentMethod = 'requires_payment_method',
}

type Status =
  | 'canceled'
  | 'processing'
  | 'requires_action'
  | 'requires_capture'
  | 'requires_confirmation'
  | 'requires_payment_method'
  | 'succeeded';

const SETUP_INTENT_PROCESSING_MAX_ATTEMPTS = 10;
const SETUP_INTENT_PROCESSING_POLL_MS = 2000;
const REDIRECT_DELAY_MS = 4000;
const TIMEOUT_REDIRECT_DELAY_MS = 3000;
const BACKEND_ERROR_GRACE_MS = 10000;

const PaymentStatus = () => {
  const {
    gift_offer_code: giftOfferCode,
    gift_recipient_stripe_customer_id: giftRecipientStripeCustomerId,
    payment_intent_client_secret: paymentIntentClientSecret,
    pause_subscription: isPauseSubscription,
    referer,
    setup_intent_client_secret: setupIntentClientSecret,
    resume_subscription: isResumeSubscription,
    update_payment: isUpdatePayment,
  } = extractQueryParams([
    'gift_offer_code',
    'gift_recipient_stripe_customer_id',
    'payment_intent_client_secret',
    'pause_subscription',
    'referer',
    'resume_subscription',
    'setup_intent_client_secret',
    'update_payment',
  ]);
  const stripe = useStripe();
  const [message, setMessage] = useState(null);
  const [subMessage, setSubMessage] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const hasHandledUpdate = useRef(false);
  const { data, refetch, isError } = useSubscriptionStatus(
    {
      referer,
      ...(giftRecipientStripeCustomerId && {
        gift_recipient_stripe_customer_id: giftRecipientStripeCustomerId,
      }),
      ...(giftOfferCode && {
        gift_offer_code: giftOfferCode,
      }),
      ...(isResumeSubscription && {
        resuming_subscription: true,
      }),
      ...(isPauseSubscription && {
        is_pause_subscription: true,
      }),
    },
    !isUpdatePayment
  );
  const { mutate: addPaymentMethod } = useAddPaymentMethod();

  const redirectToUpdatePayment = (declineError: BillingErrorPayload) => {
    billingErrorToast(declineError);
    window.location.href = update_payment_path();
  };

  const handlePaymentUpdate = useCallback(
    (setupIntent: SetupIntent | null, error: StripeError | null) => {
      if (hasHandledUpdate.current) {
        return;
      }

      const declineError = stripeSetupErrorPayload(error, setupIntent);

      if (error || !setupIntent) {
        hasHandledUpdate.current = true;
        redirectToUpdatePayment(declineError);
        return;
      }

      switch (setupIntent.status as Status) {
        case StripeStatus.Succeeded: {
          hasHandledUpdate.current = true;
          const paymentMethodId =
            typeof setupIntent.payment_method === 'string'
              ? setupIntent.payment_method
              : setupIntent.payment_method?.id;
          if (!paymentMethodId) {
            redirectToUpdatePayment(declineError);
            return;
          }
          setMessage('Confirming payment method.');
          setSubMessage('Please hold while your payment is being processed.');
          addPaymentMethod({ payment_method_id: paymentMethodId });
          break;
        }

        case StripeStatus.RequiresPaymentMethod: {
          hasHandledUpdate.current = true;
          setMessage('Your payment method was declined.');
          setSubMessage(`${declineError.message} Taking you back so you can try another card.`);
          billingErrorToast(declineError);
          setTimeout(() => {
            window.location.href = update_payment_path();
          }, REDIRECT_DELAY_MS);
          break;
        }

        default: {
          hasHandledUpdate.current = true;
          redirectToUpdatePayment(declineError);
        }
      }
    },
    [addPaymentMethod]
  );

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const userMissingPaymentIntent =
      !setupIntentClientSecret &&
      !paymentIntentClientSecret &&
      !isResumeSubscription &&
      !isPauseSubscription;
    if (userMissingPaymentIntent) {
      toast.error("You don't have permission to access this page.");
      window.location.href = root_path();
      return;
    }

    // If this is a payment method collection or update, we need to retrieve the setup intent and confirm it
    // else if this is a new subscription, we need to retrieve the subscription status
    if (setupIntentClientSecret && isUpdatePayment) {
      let cancelled = false;
      let timeoutId: ReturnType<typeof setTimeout>;

      const retrieveSetupIntent = (attempts = 0) => {
        stripe.retrieveSetupIntent(setupIntentClientSecret).then(({ setupIntent, error }) => {
          if (cancelled) {
            return;
          }

          if (
            setupIntent?.status === StripeStatus.Processing &&
            attempts < SETUP_INTENT_PROCESSING_MAX_ATTEMPTS
          ) {
            setMessage('Confirming payment method.');
            setSubMessage('This is taking a moment. Please wait.');
            timeoutId = setTimeout(
              () => retrieveSetupIntent(attempts + 1),
              SETUP_INTENT_PROCESSING_POLL_MS
            );
            return;
          }

          handlePaymentUpdate(setupIntent, error);
        });
      };

      retrieveSetupIntent();

      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
      };
    } else if (paymentIntentClientSecret || isResumeSubscription || isPauseSubscription) {
      if (isPauseSubscription) {
        setMessage('Confirming your subscription.');
        setSubMessage('Please hold while your subscription is being paused.');
      } else if (isResumeSubscription) {
        setMessage('Confirming your subscription.');
        setSubMessage('Please hold while your subscription is being resumed.');
      } else if (giftRecipientStripeCustomerId) {
        setMessage('Confirming your payment.');
        setSubMessage('Please hold while your gift subscription is being processed.');
      } else {
        setMessage('Confirming your subscription.');
        setSubMessage('Please hold while your subscription is being processed.');
      }
      const interval = setInterval(() => {
        if (elapsed >= 30000 || isError) {
          if (isPauseSubscription) {
            toast.error(
              'Pausing your subscription is taking longer than expected. Please wait a few minutes.'
            );
          } else {
            toast.error(
              'Your subscription activation is taking longer than expected. Please wait a few minutes.'
            );
          }
          clearInterval(interval);
          setTimeout(() => {
            window.location.href = root_path();
          }, TIMEOUT_REDIRECT_DELAY_MS);
        } else if (data?.success) {
          if (isPauseSubscription) {
            window.location.href = data.redirectTo;
          } else {
            trackSubscriptionInGaAndAhoy(data);
          }
          clearInterval(interval);
        } else if (data?.error_code && elapsed >= BACKEND_ERROR_GRACE_MS) {
          billingErrorToast(normalizeBillingError(data));
          clearInterval(interval);
          setTimeout(() => {
            window.location.href = subscribe_path();
          }, TIMEOUT_REDIRECT_DELAY_MS);
        } else {
          refetch();
        }
        setElapsed((prevElapsed) => prevElapsed + 5000);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [
    giftRecipientStripeCustomerId,
    data,
    elapsed,
    handlePaymentUpdate,
    isError,
    isUpdatePayment,
    paymentIntentClientSecret,
    isPauseSubscription,
    refetch,
    setupIntentClientSecret,
    stripe,
    addPaymentMethod,
    isResumeSubscription,
  ]);

  return (
    <div id="payment-status" className={clsx(styles.confirmationContainer, 'container')}>
      <div className="row">
        <div className="col-md-10 col-md-offset-1">
          <div className={styles.confirmationCard}>
            <div className={styles.loader}>
              <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} size={15} />
            </div>
            <div className={styles.message}>{message}</div>
            <div className={styles.subMessage}>{subMessage}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
